import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { PLANS } from "../data/plans";
import { useAuth, startLogin } from "../context/AuthContext";
import api from "../lib/api";
import Faq from "../components/Faq";
import Reveal from "../components/Reveal";
import { useSeo, faqLd, breadcrumbLd } from "../lib/seo";

const FAQS = [
  { q: "Can I cancel anytime?", a: "Yes. Manage or cancel your plan from your dashboard billing portal in one click. You keep access until the end of your billing period." },
  { q: "Is this a subscription?", a: "Paid plans are billed monthly or annually. Annual saves you around 30%. The free plan is free forever with 5 scans a month." },
  { q: "What happens when I hit my scan limit?", a: "Free users get 5 scans a month. When you reach the limit you'll be prompted to upgrade — your existing results stay saved." },
  { q: "Do you store my photos?", a: "Your scans are tied to your account so you can track progress. You choose whether each FaceCard is public or private, and you can delete any scan." },
  { q: "Is payment secure?", a: "Payments are processed by Stripe. We never see or store your card details." },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  useSeo({
    title: "Pricing — Free, Pro & Elite AI face analysis",
    description: "Start free with 5 scans a month. Upgrade to Pro for unlimited progress tracking, the full AI Coach plan and Future-Face projection. Cancel anytime.",
    path: "/pricing",
    jsonLd: [faqLd(FAQS), breadcrumbLd([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }])],
  });

  const choose = async (plan) => {
    if (plan.id === "free") {
      navigate("/");
      return;
    }
    if (!user) {
      startLogin();
      return;
    }
    const pkg = annual ? plan.annualPkg : plan.monthlyPkg;
    setLoadingId(plan.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <div className="container py-16">
      <Reveal>
        <div className="text-center">
          <span className="label-eyebrow">Pricing</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Simple, honest pricing.</h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">No weekly subscription traps. Start free, upgrade when you're ready to track your glow-up properly.</p>
        </div>
      </Reveal>

      <div className="mt-8 flex items-center justify-center gap-3">
        <span className={`text-sm ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          data-testid="billing-toggle"
          className="relative h-6 w-12 rounded-full bg-secondary transition"
          aria-label="Toggle annual billing"
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-primary transition-all ${annual ? "left-6" : "left-0.5"}`} />
        </button>
        <span className={`text-sm ${annual ? "text-foreground" : "text-muted-foreground"}`}>Annual <span className="text-primary">save ~30%</span></span>
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl gap-6 lg:grid-cols-3">
        {PLANS.map((p) => {
          const price = annual ? p.annual : p.monthly;
          const isCurrent = user?.plan === p.id;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`relative flex flex-col rounded-xl border bg-card p-7 ${p.popular ? "border-primary shadow-2xl" : "border-border"}`}>
              {p.popular && <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Most popular</span>}
              <h3 className="font-display text-2xl">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
              <p className="mt-5 font-display text-4xl">
                ${price}
                <span className="text-base text-muted-foreground">/{annual ? "yr" : "mo"}</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{p.limitLabel}</p>
              <button
                onClick={() => choose(p)}
                disabled={loadingId === p.id || isCurrent}
                data-testid={`choose-${p.id}-btn`}
                className={`mt-6 rounded-md py-2.5 text-sm font-semibold transition disabled:opacity-60 ${p.popular ? "bg-primary text-primary-foreground hover:brightness-110" : "border border-border hover:border-primary/50"}`}
              >
                {isCurrent ? "Current plan" : loadingId === p.id ? "Redirecting…" : p.cta}
              </button>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
                ))}
                {p.notIncluded.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground/60"><X className="mt-0.5 h-4 w-4 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <section className="mx-auto mt-20 max-w-3xl">
        <h2 className="text-center font-display text-4xl">Pricing FAQ</h2>
        <div className="mt-8"><Faq faqs={FAQS} testId="pricing-faq" /></div>
      </section>
    </div>
  );
}
